/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ILabelProps, ILabelStyles, Label } from '@fluentui/react/lib/Label';
import { Stack } from '@fluentui/react/lib/Stack';
import { Toggle } from '@fluentui/react/lib/Toggle';
import { classNamesFunction } from '@fluentui/react/lib/Utilities';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { CalloutButton } from 'src/core/components/CalloutButton';
import { TitledSection } from 'src/core/components/TitledSection';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { mapUserRoleToUserActions } from 'src/core/utils/azureUtils';
import { LuisConstants } from 'src/core/utils/LuisConstants';
import { AppVersionSettingName } from 'src/luis/projects/apps/types/AppVersionSettings';
import { getAppVersionSettingValue } from 'src/luis/projects/apps/utils/appVersionSettingsHelpers';

const messages = defineMessages({
    trainingTitle: {
        id: 'publishPane.appVersionSettingsCard.trainingTitle',
        defaultMessage: 'Version Settings'
    },
    useNonDeterministicTrainingLabel: {
        id: 'publishPane.appVersionSettingsCard.useNonDeterministicTrainingLabel',
        defaultMessage: 'Use non-deterministic training'
    },
    normalizeDiacriticsLabel: {
        id: 'publishPane.appVersionSettingsCard.normalizeDiacriticsLabel',
        defaultMessage: 'Normalize diacritics'
    },
    normalizePunctuationLabel: {
        id: 'publishPane.appVersionSettingsCard.normalizePunctuationLabel',
        defaultMessage: 'Normalize punctuation'
    },
    normalizeWordFormLabel: {
        id: 'publishPane.appVersionSettingsCard.normalizeWordFormLabel',
        defaultMessage: 'Normalize word forms'
    },
    on: {
        id: 'publishPane.appVersionSettingsCard.on',
        defaultMessage: 'On'
    },
    recommended: {
        id: 'publishPane.appVersionSettingsCard.recommended',
        defaultMessage: 'recommended'
    },
    off: {
        id: 'publishPane.appVersionSettingsCard.off',
        defaultMessage: 'Off'
    },
    useNonDeterministicTrainingMessage: {
        id: 'publishPane.appVersionSettingsCard.useNonDeterministicTrainingMessage',
        defaultMessage: 'When disabled, training uses all data instead of the small negative sampling when training.'
    },
    normalizeDiacriticsMessage: {
        id: 'publishPane.appVersionSettingsCard.normalizeDiacriticsMessage',
        defaultMessage: 'When enabled, diacritics do not affect prediction score.'
    },
    normalizePunctuationMessage: {
        id: 'publishPane.appVersionSettingsCard.normalizePunctuationMessage',
        defaultMessage: 'When enabled, punctuation does not affect prediction score'
    },
    normalizeWordFormMessage: {
        id: 'publishPane.appVersionSettingsCard.normalizeWordFormMessage',
        defaultMessage: 'Use normalized word forms as features in intent predictions'
    },
    updateSettingsToastStart: {
        id: 'publishPane.appVersionSettingsCard.updateSettingsToastStart',
        defaultMessage: 'Updating settings ...'
    },
    updateSettingsToastSuccess: {
        id: 'publishPane.appVersionSettingsCard.updateSettingsToastSuccess',
        defaultMessage: 'Update settings completed'
    },
    updateSettingsToastFailed: {
        id: 'publishPane.appVersionSettingsCard.updateSettingsToastFailed',
        defaultMessage: 'Update settings failed'
    }
});

const flexLabelStyles = classNamesFunction<ILabelProps, ILabelStyles>()({ root: { display: 'flex' } });

/**
 * The component responsible for the conversation application's
 * settings in the application settings page in the manage pane.
 */
export const AppVersionSettingsCard = observer(() => {
    const { dispatcher, rootStore } = useGlobalState();
    const { localize } = useLocalization();
    const currentAppStore = rootStore.currentLuisAppStore;
    const currentAppVersionSettings = currentAppStore.currentAppVersionStore.settings;

    const useAllTrainingData = getAppVersionSettingValue(currentAppVersionSettings, 'UseAllTrainingData');
    const normalizeDiacritics = getAppVersionSettingValue(currentAppVersionSettings, 'NormalizeDiacritics');
    const normalizePunctuation = getAppVersionSettingValue(currentAppVersionSettings, 'NormalizePunctuation');
    const normalizeWordForm = getAppVersionSettingValue(currentAppVersionSettings, 'NormalizeWordForm');

    const { userRole } = rootStore.serviceKey;
    const { canWrite } = mapUserRoleToUserActions(userRole);

    const showNormalizedDiacritics = LuisConstants.Luis.Cultures.NormalizedDiacriticsSupportedCultures.some(
        c => c === currentAppStore.culture
    );
    const showNormalizedWordForms = LuisConstants.Luis.Cultures.NormalizedWordFormsSupportedCultures.some(
        c => c === currentAppStore.culture
    );

    const updateConversationAppVersionSetting = (name: AppVersionSettingName, value: boolean) => {
        dispatcher.dispatch('updateAppVersionSettings', {
            settings: [{ name, value: value.toString() }],
            toastData: {
                running: { title: localize(messages.updateSettingsToastStart) },
                completed: { title: localize(messages.updateSettingsToastSuccess) },
                failed: { title: localize(messages.updateSettingsToastFailed) }
            }
        });
    };

    return (
        <TitledSection title={localize(messages.trainingTitle)}>
            <Stack tokens={{ childrenGap: 20 }}>
                <Stack verticalAlign="baseline">
                    <Label styles={flexLabelStyles}>
                        {localize(messages.useNonDeterministicTrainingLabel)}
                        <CalloutButton content={localize(messages.useNonDeterministicTrainingMessage)} />
                    </Label>

                    <Toggle
                        data-automation-id="appVersionSettingsUseAllTraining"
                        checked={useAllTrainingData !== 'true'}
                        onChange={(_, checked: boolean) => updateConversationAppVersionSetting('UseAllTrainingData', !checked)}
                        onText={`${localize(messages.on)}   (${localize(messages.recommended)})`}
                        offText={localize(messages.off)}
                        disabled={!canWrite}
                    />
                </Stack>
                {showNormalizedDiacritics && (
                    <Stack verticalAlign="baseline">
                        <Label styles={flexLabelStyles}>
                            {localize(messages.normalizeDiacriticsLabel)}
                            <CalloutButton content={localize(messages.normalizeDiacriticsMessage)} />
                        </Label>

                        <Toggle
                            data-automation-id="appVersionSettingsNormalizeDiacritics"
                            checked={normalizeDiacritics === 'true'}
                            onChange={(_, checked: boolean) => updateConversationAppVersionSetting('NormalizeDiacritics', checked)}
                            onText={`${localize(messages.on)}`}
                            offText={localize(messages.off)}
                            disabled={!canWrite}
                        />
                    </Stack>
                )}
                <Stack verticalAlign="baseline">
                    <Label styles={flexLabelStyles}>
                        {localize(messages.normalizePunctuationLabel)}
                        <CalloutButton content={localize(messages.normalizePunctuationMessage)} />
                    </Label>

                    <Toggle
                        data-automation-id="appVersionSettingsNormalizePunctuation"
                        checked={normalizePunctuation === 'true'}
                        onChange={(_, checked: boolean) => updateConversationAppVersionSetting('NormalizePunctuation', checked)}
                        onText={`${localize(messages.on)}`}
                        offText={localize(messages.off)}
                        disabled={!canWrite}
                    />
                </Stack>
                {showNormalizedWordForms && (
                    <Stack verticalAlign="baseline">
                        <Label styles={flexLabelStyles}>
                            {localize(messages.normalizeWordFormLabel)}
                            <CalloutButton content={localize(messages.normalizeWordFormMessage)} />
                        </Label>

                        <Toggle
                            data-automation-id="appVersionSettingsNormalizeWordForm"
                            checked={normalizeWordForm === 'true'}
                            onChange={(_, checked: boolean) => updateConversationAppVersionSetting('NormalizeWordForm', checked)}
                            onText={`${localize(messages.on)}`}
                            offText={localize(messages.off)}
                            disabled={!canWrite}
                        />
                    </Stack>
                )}
            </Stack>
        </TitledSection>
    );
});
